import Vue from 'vue'
import Vuex from 'vuex'
import $axios from '@/plugins/axios'
import router from '@/router'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: null,
    categories: [],
    products: [],
    product: {},
    orders: [],
    order: {},
    colors: [],
    materials: [],
  },
  getters: {
    getProducts(state) {
      return state.products
    },
    getProduct(state) {
      return state.product
    },
    getCategories(state) {
      return state.categories
    },
    getOrders(state) {
      return state.orders
    },
    getOrder(state) {
      return state.order
    },
    getColors(state) {
      return state.colors
    },
    getMaterials(state) {
      return state.materials
    },
  },
  mutations: {
    setProducts(state, products) {
      state.products = products
    },
    setProduct(state, product) {
      state.product = product
    },
    setCategories(state, categories) {
      state.categories = categories
    },
    setToken(state, token) {
      state.token = token
    },
    setUser(state, user) {
      state.user = user
    },
    setOrders(state, orders) {
      state.orders = orders
    },
    setOrder(state, order) {
      state.order = order
    },
    setColors(state, colors) {
      state.colors = colors
    },
    setMaterials(state, materials) {
      state.materials = materials
    },
  },
  actions: {
    // Auth
    login({ commit }, payload) {
      return $axios.post('email-login', payload).then((res) => {
        commit('setUser', res.data.user)
        router.push({ name: 'dashboard' })
      })
    },

    logout({ commit }) {
      return $axios.post('logout').then((res) => {
        commit('setToken', null)
        commit('setUser', null)
      })
    },

    register({ commit }, payload) {
      return $axios.post('register', payload).then((res) => {
        commit('setToken', res.data.access_token)
        commit('setUser', res.data.user)
      })
    },

    // Products
    getProducts({ commit }, search) {
      let page = search.page ?? 1
      return $axios.get(`admin/products?page=${page}&sort=${search.sort}`).then((res) => {
        commit('setProducts', res.data)
      })
    },

    fetchProducts({ commit }, search) {
      return $axios.get(`admin/products?page=${search.page}&category=${search.category}&sort=${search.sort}&title=${search.keyword}`).then((res) => {
        commit('setProducts', res.data)
      })
    },

    showProduct({ commit }, id) {
      return $axios.get(`admin/products/${id}`)
    },

    saveProduct({ commit }, payload) {
      return $axios.post('admin/products', payload)
    },

    updateProduct({ commit }, payload) {
      if (payload.categories && payload.categories.length > 0 && payload.categories[0].id) {
        payload.categories = payload.categories.map((category) => {
          return category.id
        })
      }

      // Transform colors from objects to IDs if needed
      if (payload.colors && payload.colors.length > 0 && payload.colors[0]?.id) {
        payload.colors = payload.colors.map((color) => {
          return color.id
        })
      }

      return $axios.patch(`admin/products/${payload.id}`, payload)
    },

    deleteProduct({ commit }, payload) {
      return $axios.delete(`admin/products/${payload}`)
    },

    saveVariations({ commit }, payload) {
      return $axios.post(`admin/products/${payload.id}/variations`, payload.variations)
    },

    updateVariations({ commit }, payload) {
      return $axios.post(`admin/products/${payload.id}/variations`, payload.variations)
    },

    deleteVariation({ commit }, id) {
      return $axios.delete(`admin/variations/${id}`)
    },

    saveCategory({ commit }, payload) {
      return $axios.post('admin/categories', payload)
    },

    updateCategory({ commit }, payload) {
      return $axios.patch(`admin/categories/${payload.id}`, payload)
    },

    deleteCategory({ commit }, id) {
      return $axios.delete(`admin/categories/${id}`)
    },

    // upload image for Product
    uploadImage({ commit }, payload) {
      return $axios.post(`admin/image`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },

    deleteImage({ commit }, url) {
      return $axios.delete(`admin/image?address=${url}`)
    },

    reorderImages({ commit }, payload) {
      return $axios.post('admin/images/reorder', payload)
    },

    // Dashboard
    fetchDashboardStatistics({ commit }) {
      return $axios.get('admin/dashboard/statistics')
    },

    // Categories
    getCategories({ commit }) {
      return $axios.get('admin/categories').then((res) => {
        commit('setCategories', res.data)
      })
    },

    // Orders
    getOrders({ commit }, params = {}) {
      const page = params.page || 1
      const search = params.search || ''

      let url = `admin/orders?page=${page}`
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }

      return $axios.get(url).then((res) => {
        commit('setOrders', res.data)
      })
    },
    fetchOrder({ commit }, uuid) {
      return $axios.get(`admin/orders/uuid/${uuid}`).then((res) => {
        commit('setOrder', res.data.data)
      })
    },
    updateOrder({ commit }, payload) {
      return $axios.patch(`admin/orders/${payload.uuid}`, payload)
    },

    // Colors
    fetchColors({ commit }) {
      return $axios.get(`admin/colors`).then((res) => {
        commit('setColors', res.data.data)
      })
    },

    getColor({ commit }, id) {
      return $axios.get(`admin/colors/${id}`).then((res) => {
        return res.data.data
      })
    },

    createColor({ commit }, payload) {
      return $axios.post('admin/colors', payload).then(() => {
        // Refresh colors list
        return $axios.get('admin/colors').then((res) => {
          commit('setColors', res.data.data)
        })
      })
    },

    updateColor({ commit }, { id, data }) {
      // Check if data is FormData (has append method) or regular object
      const isFormData = data instanceof FormData

      console.log('updateColor called with:', { id, isFormData })
      console.log('Data type:', typeof data)
      console.log('Data instanceof FormData:', isFormData)

      const config = isFormData
        ? {} // Let browser set Content-Type for FormData
        : {
            headers: {
              'Content-Type': 'application/json',
            },
          }

      console.log('Request config:', config)
      console.log('Request URL:', `admin/colors/${id}`)

      return $axios.patch(`admin/colors/${id}`, data, config).then(() => {
        // Refresh colors list
        return $axios.get('admin/colors').then((res) => {
          commit('setColors', res.data.data)
        })
      })
    },

    deleteColor({ commit }, id) {
      return $axios.delete(`admin/colors/${id}`).then(() => {
        // Refresh colors list
        return $axios.get('admin/colors').then((res) => {
          commit('setColors', res.data.data)
        })
      })
    },

    // Payments
    getPayments({ commit }) {
      return $axios.get('admin/payments')
    },

    // Users
    getUsers({ commit }, page = 1) {
      return $axios.get(`admin/users?page=${page}`)
    },

    // Materials
    fetchMaterials({ commit }) {
      return $axios.get(`admin/materials`).then((res) => {
        commit('setMaterials', res.data.data)
      })
    },

    createMaterial({ commit }, payload) {
      return $axios.post('admin/materials', payload).then(() => {
        // Refresh materials list
        return $axios.get('admin/materials').then((res) => {
          commit('setMaterials', res.data.data)
        })
      })
    },

    updateMaterial({ commit }, payload) {
      return $axios.patch(`admin/materials/${payload.id}`, payload).then(() => {
        // Refresh materials list
        return $axios.get('admin/materials').then((res) => {
          commit('setMaterials', res.data.data)
        })
      })
    },

    deleteMaterial({ commit }, id) {
      return $axios.delete(`admin/materials/${id}`).then(() => {
        // Refresh materials list
        return $axios.get('admin/materials').then((res) => {
          commit('setMaterials', res.data.data)
        })
      })
    },
  },
  modules: {},
})
